import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { get, map } from 'lodash/fp';
import { CheckBoxOutlineBlank, RadioButtonUnchecked } from '@material-ui/icons';
import messages from '../messages';

const S = {
  QuestionBlock: styled.div.attrs(({ position, size }) => ({
    style: {
      top: `${position.y}%`,
      left: `${position.x}%`,
      width: `${size.width}%`,
      height: `${size.height}%`,
    },
  }))`
    position: absolute;
    border: 2px solid ${({ theme }) => theme.palette.status.note.main};
    border-radius: 2px;
    box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.24);
  `,
  QuestionNumber: styled.div`
    position: absolute;
    top: 0;
    left: 0;
    transform: translate(calc(-100% - 2px), calc(-100% - 2px));
    padding: 4px 6px;
    border-radius: 12px 12px 0 12px;
    color: ${({ theme }) => theme.palette.primary.contrastText};
    background-color: ${({ theme }) => theme.palette.status.note.main};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    font-weight: 700;
    line-height: 1.54;
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.32);
  `,
  OptionItem: styled.li`
    position: absolute;
    top: ${({ position }) => `${position.y}%`};
    left: ${({ position }) => `${position.x}%`};
    display: flex;
    align-items: center;
  `,
  Mesh: styled.div`
    position: relative;
    display: flex;
    ::before {
      content: '';
      position: absolute;
      top: 50%;
      right: 4px;
      transform: translateY(-50%);
      width: 16px;
      height: 16px;
      opacity: 0.54;
      border-radius: 50%;
      background-color: ${({ theme }) => theme.palette.primary.contrastText};
      ${({ rounded }) => rounded && css`
          right: 2px;
          width: 20px;
          height: 20px;
          border-radius: 50%;
        `}
    }
  `,
  OptionNumber: styled.div`
    min-width: 32px;
    padding: 1px 4px 2px;
    border-radius: 2px;
    color: ${({ theme }) => theme.palette.primary.contrastText};
    background-color: ${({ theme }) => theme.palette.primary.light};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    font-weight: 500;
    text-align: center;
    white-space: nowrap;
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.32);
  `,
  CheckBoxOutlineBlank: styled(CheckBoxOutlineBlank)`
    position: relative;
    color: ${({ theme }) => theme.palette.grey[600]};
  `,
  RadioButtonUnchecked: styled(RadioButtonUnchecked)`
    position: relative;
    color: ${({ theme }) => theme.palette.grey[600]};
  `,
  FieldRect: styled.div.attrs(({ field }) => ({
    style: {
      top: `${field.y}%`,
      left: `${field.x}%`,
      width: `${field.width}%`,
    },
  }))`
    position: absolute;
    padding: 0 8px;
    overflow: hidden;
    color: ${({ theme }) => theme.palette.primary.light};
    background-color: #e5e7f0;
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    font-weight: 500;
    box-shadow: 0 1px 2px 0 rgba(93, 108, 164, 0.54);
    text-overflow: ellipsis;
    white-space: nowrap;
    ::after {
      content: '';
      position: absolute;
      right: 2px;
      bottom: 2px;
      border: 4px solid ${({ theme }) => theme.palette.primary.light};
      border-top-color: transparent;
      border-left-color: transparent;
    }
  `,
  OtherResponseRect: styled.div.attrs(({ field, viewportWidth }) => ({
    style: {
      top: `${field.position.y}%`,
      left: `${field.position.x}%`,
      width: `${(field.size.width / 100) * viewportWidth}px`,
    },
  }))`
    overflow: hidden;
    height: 20px;
    padding: 2px 4px;
    color: ${({ theme }) => theme.palette.primary.light};
    background-color: #e5e7f0;
    font-size: ${({ theme }) => theme.typography.pxToRem(10)};
    font-weight: 500;
    line-height: 1.56;
    box-shadow: 0 1px 2px 0 rgba(93, 108, 164, 0.54);
    white-space: nowrap;
    text-overflow: ellipsis;
  `,
};

const QuestionRenderer = ({ questions, viewportWidth }) => questions && (
  <ol>
    {map(question => (
      <li key={question.number}>
        <S.QuestionBlock
          number={question.number}
          position={question.position || { x: question.offsetX, y: question.offsetY }}
          required={question.required}
          size={question.size || { width: 0, height: 0 }}
        >
          <S.QuestionNumber>
            {`Q${question.number}${question.required ? '*' : ''}`}
          </S.QuestionNumber>
        </S.QuestionBlock>
        {question.options && (
          <ul>
            {map(option => (
              // XXX: 하위 호환성 유지를 위한 필드. 추후 제거 예정
              <S.OptionItem
                key={option.number}
                position={option.position || { x: option.offsetX, y: option.offsetY }}
                rounded={question.type === 'SINGLE_SELECTION'}
              >
                <S.OptionNumber>
                  {`${question.number}-${option.number}`}
                </S.OptionNumber>
                <S.Mesh>
                  {question.type === 'SINGLE_SELECTION'
                    ? <S.RadioButtonUnchecked /> : <S.CheckBoxOutlineBlank />}
                </S.Mesh>
                {option.type === 'TEXT' && (
                  <S.OtherResponseRect
                    field={{ position: option.position, size: option.size || 25 }}
                    viewportWidth={viewportWidth}
                    size="small"
                  >
                    기타 의견을 입력하세요.
                  </S.OtherResponseRect>
                )}
              </S.OptionItem>
            ), question.options)}
          </ul>
        )}
        {question.field && (
          <S.FieldRect
            field={get('x', question.field)
              ? question.field
              : { x: question.field.offsetX, y: question.field.offsetY, width: 20 }}
          >
            <FormattedMessage
              {...messages.questionRendererFieldRect}
              values={{ number: question.number }}
            />
          </S.FieldRect>
        )}
      </li>
    ), questions)}
  </ol>
);

QuestionRenderer.propTypes = {
  questions: PropTypes.arrayOf(PropTypes.shape({
    number: PropTypes.number.isRequired,
  })),
  viewportWidth: PropTypes.number,
};

QuestionRenderer.defaultProps = {
  questions: null,
  viewportWidth: 0,
};

export default QuestionRenderer;
